import AppConstants from "../constants/AppConstants";
import ElementTypeMaps from "../constants/ElementTypeMaps";
import ModelFactory from "../models/ModelFactory";
import UIActions from "../actions/UIActions";
import UserActions from "../actions/UserActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";

const { ELEMENT_TYPE_SIMPLE } = AppConstants;

const CLICK_LOCATION_FOOTER = "footer";
/** `elementType` PostHog gets for a Submit Button, which is stored as an ordinary button. */
const TRACK_AS_SUBMIT_BUTTON = "submit button";

const _toggleElementPropertiesSection = (elementType) => {
  const panelSection = ElementTypeMaps[elementType];
  if (panelSection) {
    UIActions.toggleElementPropertiesSection({ name: panelSection });
  }
};

/**
 * Adds a freshly built element to the page, tracks it and opens its properties section.
 * @param {Object} element
 * @param {{ containedBy?: string, blockId?: string }} target
 * @param {string} clickLocation
 * @param {string} [trackAs] - `elementType` reported to PostHog; defaults to the element's type.
 * @param {Object} [trackProps] - Extra properties for the PostHog event.
 */
const _insertElement = (
  element,
  { containedBy, blockId },
  clickLocation,
  trackAs = element.type,
  trackProps = {},
) => {
  UserActions.trackPostHogEvent("add_element", {
    clickLocation,
    elementType: trackAs,
    ...trackProps,
  });
  DocumentStore.dispatch({
    type: COMMANDS.ADD_ELEMENT,
    payload: { element, isNew: true, containedBy, blockId },
  });
  _toggleElementPropertiesSection(element.type);
};

/**
 * @param {string} style
 * @param {{ containedBy?: string, blockId?: string }} [target]
 * @param {string} [clickLocation="footer"]
 */
const addTextElement = (style, target = {}, clickLocation = CLICK_LOCATION_FOOTER) =>
  _insertElement(ModelFactory.getTextElement({ style }), target, clickLocation);

/**
 * @param {string} elementType
 * @param {{ containedBy?: string, blockId?: string }} [target]
 * @param {string} [clickLocation="footer"]
 * @param {Object} [content] replaces the model's default content
 */
const addElement = (elementType, target = {}, clickLocation = CLICK_LOCATION_FOOTER, content) => {
  const element = ModelFactory.getElement(elementType);
  if (content) {
    element.content = content;
  }
  _insertElement(element, target, clickLocation);
};

/**
 * @param {{ containedBy?: string, blockId?: string }} [target]
 * @param {string} [clickLocation="footer"]
 */
const addButton = (target = {}, clickLocation = CLICK_LOCATION_FOOTER) =>
  _insertElement(ModelFactory.getButton(), target, clickLocation);

/**
 * @param {{ containedBy?: string, blockId?: string }} [target]
 * @param {string} [clickLocation="footer"]
 */
const addOvalElement = (target = {}, clickLocation = CLICK_LOCATION_FOOTER) => {
  const { containedBy, blockId } = target;
  const element = ModelFactory.getElement(ELEMENT_TYPE_SIMPLE);
  UserActions.trackPostHogEvent("add_element", { clickLocation, elementType: "oval" });
  DocumentStore.dispatch({
    type: COMMANDS.ADD_ELEMENT,
    payload: { element, isNew: true, containedBy, blockId },
  });
  element.desktop = {
    ...element.desktop,
    border: { rad: "50%" },
  };
  _toggleElementPropertiesSection(ELEMENT_TYPE_SIMPLE);
};

/**
 * @param {{ type: string, dataType?: string, multiLine?: boolean }} field
 * @param {{ containedBy?: string, blockId?: string }} [target]
 * @param {string} [clickLocation="footer"]
 */
const addFormFieldElement = (field, target = {}, clickLocation = CLICK_LOCATION_FOOTER) => {
  const element = ModelFactory.getFormFieldElement(field);
  // Only inputs have a dataType; the other field types would report undefined.
  const trackProps = element.content?.dataType ? { dataType: element.content.dataType } : {};
  _insertElement(element, target, clickLocation, element.type, trackProps);
};

/**
 * @param {{ containedBy?: string, blockId?: string }} [target]
 * @param {string} [clickLocation="footer"]
 */
const addSubmitButton = (target = {}, clickLocation = CLICK_LOCATION_FOOTER) =>
  _insertElement(ModelFactory.getSubmitButton(), target, clickLocation, TRACK_AS_SUBMIT_BUTTON);

export {
  addTextElement,
  addElement,
  addButton,
  addOvalElement,
  addFormFieldElement,
  addSubmitButton,
};
